import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";

const statusConfig = {
  active: { label: "Active", color: "var(--color-success)", bg: "rgba(34,197,94,0.12)" },
  trial: { label: "Trial", color: "var(--color-primary)", bg: "rgba(99,102,241,0.12)" },
  expired: { label: "Expired", color: "var(--color-warning)", bg: "rgba(234,179,8,0.12)" },
  cancelled: { label: "Cancelled", color: "var(--color-error)", bg: "rgba(239,68,68,0.12)" },
};

const emptyForm = {
  vendor_id: "",
  plan_id: "",
  billing_cycle: "monthly",
  payment_status: "trial",
  start_date: new Date()?.toISOString()?.split("T")?.[0],
  end_date: "",
  notes: "",
};

export default function VendorPlanAssignment() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [plans, setPlans] = useState([]);
  const [usageMap, setUsageMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [vendorRes, planRes, subRes] = await Promise.all([
        api.get('/super-admin/vendors'),
        api.get('/super-admin/plans'),
        api.get('/super-admin/subscriptions'),
      ]);

      setVendors(vendorRes?.data || []);
      setPlans(planRes?.data || []);
      setSubscriptions(subRes?.data || []);
      setUsageMap({});
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openAssignModal = (sub = null) => {
    setEditingSub(sub);
    if (sub) {
      setForm({
        vendor_id: sub?.vendor_id || "",
        plan_id: sub?.plan_id || "",
        billing_cycle: sub?.billing_cycle || "monthly",
        payment_status: sub?.payment_status || "trial",
        start_date: sub?.start_date || new Date()?.toISOString()?.split("T")?.[0],
        end_date: sub?.end_date || "",
        notes: sub?.notes || "",
      });
    } else {
      setForm(emptyForm);
    }
    setFormError(null);
    setShowFormModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    setFormError(null);
    if (!form?.vendor_id) return setFormError("Please select a vendor.");
    if (!form?.plan_id) return setFormError("Please select a plan.");
    if (!form?.start_date) return setFormError("Start date is required.");

    setFormLoading(true);
    try {
      const payload = {
        vendor_id: form?.vendor_id,
        plan_id: form?.plan_id,
        billing_cycle: form?.billing_cycle,
        payment_status: form?.payment_status,
        start_date: form?.start_date,
        end_date: form?.end_date || null,
        notes: form?.notes?.trim() || null,
        updated_at: new Date()?.toISOString(),
      };

      const { updated_at, ...cleanPayload } = payload;
      await api.post('/super-admin/subscriptions', cleanPayload);

      await fetchAll();
      setShowFormModal(false);
    } catch (err) {
      setFormError(err?.message || "An error occurred.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (subId) => {
    // No delete endpoint for subscriptions
    console.error("Subscription delete not supported by backend");
  };

  const filteredSubs = subscriptions?.filter((s) => {
    const vendorName = s?.vendor_name || s?.gym_name || "";
    const planName = s?.plan_name || "";
    const matchSearch =
      vendorName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      planName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = filterStatus === "all" || s?.payment_status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Vendors without a subscription
  const assignedVendorIds = new Set(subscriptions?.map((s) => s?.vendor_id));
  const unassignedVendors = vendors?.filter((v) => !assignedVendorIds?.has(v?.id));

  const formatLimit = (val) => (val === -1 ? "∞" : val ?? "—");

  const UsageBar = ({ used, max, label }) => {
    if (max === -1) return <span className="text-[10px] text-[var(--color-muted-foreground)]">{used}/∞ {label}</span>;
    const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
    const color = pct >= 90 ? "var(--color-error)" : pct >= 70 ? "var(--color-warning)" : "var(--color-success)";
    return (
      <div className="flex items-center gap-1.5">
        <div className="w-16 h-1.5 bg-[var(--color-muted)] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        <span className="text-[10px] text-[var(--color-muted-foreground)]">{used}/{max} {label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Unassigned Vendors Alert */}
      {unassignedVendors?.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 rounded-[var(--radius-md)]">
          <Icon name="AlertTriangle" size={15} color="var(--color-warning)" />
          <p className="text-xs text-[var(--color-foreground)] font-[var(--font-caption)]">
            <strong>{unassignedVendors?.length}</strong> vendor{unassignedVendors?.length !== 1 ? "s" : ""} without a subscription plan:&nbsp;
            {unassignedVendors?.slice(0, 3)?.map((v) => v?.gym_name || v?.full_name)?.join(", ")}
            {unassignedVendors?.length > 3 && ` +${unassignedVendors?.length - 3} more`}
          </p>
          <button
            onClick={() => openAssignModal()}
            className="ml-auto flex-shrink-0 px-2.5 py-1 text-xs font-medium bg-[var(--color-warning)] text-white rounded-md hover:opacity-90 transition-opacity"
          >
            Assign Now
          </button>
        </div>
      )}
      {/* Main Table */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[var(--color-border)]">
          <div>
            <h2 className="text-base font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              Vendor Plan Assignments
            </h2>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
              {filteredSubs?.length} subscription{filteredSubs?.length !== 1 ? "s" : ""} · track usage vs limits per vendor
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-2.5 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="relative">
              <Icon name="Search" size={13} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search vendor or plan..."
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="pl-8 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] w-48"
              />
            </div>
            <button
              onClick={() => openAssignModal()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
            >
              <Icon name="Plus" size={13} color="white" />
              Assign Plan
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-3 p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md text-xs text-[var(--color-error)]">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[var(--color-muted)]/40 border-b border-[var(--color-border)]">
                {["Vendor", "Plan", "Billing", "Usage (Branches/Members/Staff)", "Status", "Expiry", "Actions"]?.map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 4 })?.map((_, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)]">
                    {Array.from({ length: 7 })?.map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3 bg-[var(--color-muted)] rounded animate-pulse w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredSubs?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="UserCheck" size={28} color="var(--color-muted-foreground)" />
                      <p>No vendor subscriptions found.</p>
                      <button onClick={() => openAssignModal()} className="text-[var(--color-primary)] underline text-xs">
                        Assign a plan to a vendor
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSubs?.map((sub) => {
                  const plan = sub?.subscription_plans;
                  const vendor = sub?.profiles;
                  const usage = usageMap?.[sub?.vendor_id] || { branches: 0, members: 0, staff: 0, trainers: 0 };
                  const cfg = statusConfig?.[sub?.payment_status] || statusConfig?.trial;
                  const isExpired = sub?.end_date && new Date(sub?.end_date) < new Date();

                  return (
                    <tr key={sub?.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-muted)]/20 transition-colors">
                      {/* Vendor */}
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">
                          {vendor?.gym_name || vendor?.full_name || "—"}
                        </p>
                        <p className="text-[10px] text-[var(--color-muted-foreground)]">{vendor?.email}</p>
                      </td>

                      {/* Plan */}
                      <td className="px-4 py-3">
                        <p className="font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">
                          {plan?.plan_name || "—"}
                        </p>
                        <p className="text-[10px] text-[var(--color-muted-foreground)]">
                          ${sub?.billing_cycle === "yearly" ? plan?.price_yearly : plan?.price_monthly}/{sub?.billing_cycle === "yearly" ? "yr" : "mo"}
                        </p>
                      </td>

                      {/* Billing */}
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[var(--color-muted)] text-[var(--color-foreground)] capitalize">
                          {sub?.billing_cycle}
                        </span>
                      </td>

                      {/* Usage */}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <UsageBar used={usage?.branches} max={plan?.max_branches ?? 0} label="br" />
                          <UsageBar used={usage?.members} max={plan?.max_members ?? 0} label="mem" />
                          <UsageBar used={usage?.staff} max={plan?.max_staff ?? 0} label="staff" />
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-semibold font-[var(--font-caption)]"
                          style={{ backgroundColor: cfg?.bg, color: cfg?.color }}
                        >
                          {cfg?.label}
                        </span>
                        {isExpired && sub?.payment_status === "active" && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-[var(--color-error)]/10 text-[var(--color-error)]">
                            Overdue
                          </span>
                        )}
                      </td>

                      {/* Expiry */}
                      <td className="px-4 py-3 text-[var(--color-foreground)]">
                        {sub?.end_date ? (
                          <span className={isExpired ? "text-[var(--color-error)]" : ""}>
                            {new Date(sub?.end_date)?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        ) : (
                          <span className="text-[var(--color-muted-foreground)]">No expiry</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openAssignModal(sub)}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-[var(--color-primary)]/10 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/20 transition-colors"
                          >
                            <Icon name="RefreshCw" size={11} color="currentColor" />
                            Reassign
                          </button>
                          <button
                            onClick={() => handleDelete(sub?.id)}
                            className="p-1.5 rounded-md hover:bg-[var(--color-error)]/10 text-[var(--color-muted-foreground)] hover:text-[var(--color-error)] transition-colors"
                            title="Remove"
                          >
                            <Icon name="Trash2" size={12} color="currentColor" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Assign/Edit Modal */}
      {showFormModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !formLoading && setShowFormModal(false)} />
          <div className="relative bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-2xl)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)]">
              <h3 className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
                {editingSub ? "Reassign / Upgrade Plan" : "Assign Plan to Vendor"}
              </h3>
              <button onClick={() => !formLoading && setShowFormModal(false)} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="p-5 space-y-4">
              {formError && (
                <div className="p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md text-xs text-[var(--color-error)]">
                  {formError}
                </div>
              )}

              {/* Vendor */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Vendor *</label>
                <select
                  name="vendor_id"
                  value={form?.vendor_id}
                  onChange={handleFormChange}
                  disabled={!!editingSub}
                  className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] disabled:opacity-60"
                >
                  <option value="">Select vendor...</option>
                  {vendors?.map((v) => (
                    <option key={v?.id} value={v?.id}>
                      {v?.gym_name || v?.full_name} ({v?.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Subscription Plan *</label>
                <select
                  name="plan_id"
                  value={form?.plan_id}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                >
                  <option value="">Select plan...</option>
                  {plans?.filter((p) => p?.is_active)?.map((p) => (
                    <option key={p?.id} value={p?.id}>
                      {p?.plan_name} — ${p?.price_monthly}/mo · {p?.max_branches === -1 ? "∞" : p?.max_branches} branches · {p?.max_members === -1 ? "∞" : p?.max_members} members
                    </option>
                  ))}
                </select>
              </div>

              {/* Billing + Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Billing Cycle</label>
                  <select
                    name="billing_cycle"
                    value={form?.billing_cycle}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Payment Status</label>
                  <select
                    name="payment_status"
                    value={form?.payment_status}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  >
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={form?.start_date}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={form?.end_date}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-[var(--color-foreground)] mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={form?.notes}
                  onChange={handleFormChange}
                  rows={2}
                  placeholder="Optional notes about this subscription..."
                  className="w-full px-3 py-2 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => !formLoading && setShowFormModal(false)}
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-xs font-medium hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
                >
                  {formLoading ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
                  ) : (
                    <>{editingSub ? "Update Assignment" : "Assign Plan"}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
